import { Fragment, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModel } from '../lib/hooks.js';
import { useAlerts, type Alert } from '../lib/alerts.js';
import { Icon } from '../lib/icons.js';
import { openHistory } from '../lib/ui-state.js';
import { roomHref } from '../lib/spaces.js';
import { AlertItems } from '../components/alerts/AlertStrip.js';

/** Warnings live in the sidebar (desktop) and the ☰ drawer (phones), not on pages. */
export function SidebarWarnings({ home, onNavigate }: { home: string; onNavigate?: () => void }): ReactNode {
  const alerts = useAlerts(home);
  const model = useModel(home);
  const navigate = useNavigate();
  if (!alerts.length) return null;
  const open = (a: Alert): void => {
    onNavigate?.();
    if (a.entityId) openHistory(home, a.entityId);
    else if (a.roomId) navigate(roomHref(home, model, a.roomId));
  };
  return (
    <details className="side-group side-warnings" data-testid="alert-strip">
      <summary className="side-label side-warnings-summary">
        <span className="side-warnings-dot" data-tone={alerts.some((a) => a.tone === 'alert') ? 'alert' : 'caution'} />
        Warnings ({alerts.length})
      </summary>
      {alerts.map((a) => (
        <Fragment key={a.id}>
          <button type="button" className="side-warning" data-tone={a.tone} onClick={() => open(a)} data-testid={`alert-${a.id}`}>
            <Icon name={a.icon} size={15} />
            <span className="side-warning-text">
              <strong>{a.title}</strong>
              {a.detail && <span>{a.detail}</span>}
            </span>
          </button>
          {a.items?.length ? <AlertItems items={a.items} /> : null}
        </Fragment>
      ))}
    </details>
  );
}

/** Small count badge for the ☰ button so phone users know the drawer has warnings. */
export function WarningsBadge({ home }: { home: string }): ReactNode {
  const alerts = useAlerts(home);
  if (!alerts.length) return null;
  const tone = alerts.some((a) => a.tone === 'alert') ? 'alert' : 'caution';
  return (
    <span className="menu-badge" data-tone={tone} aria-label={`${alerts.length} warnings`} data-testid="menu-warnings">
      {alerts.length}
    </span>
  );
}
